import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import Select from "react-select";
import { TextField } from "@material-ui/core";
import { Alert, Snackbar } from "@mui/material";
import WarehouseService from "../../services/WarehouseService";
import getCurrentUserId from "../../reduxSaga/sagas/requests/currentUserId";
import withLoader from "../../components/layouts/WithLoader";
import * as XLSX from "xlsx";

const ALL_WAREHOUSES = { value: "", label: "Tous" };
const DEFAULT_PAGE_SIZE = 20;
// maximum page size accepted by the API
const EXPORT_PAGE_SIZE = 100;

// whole months between a date and today
const monthsSince = (date) => {
  const from = new Date(date);
  const now = new Date();
  let months =
    (now.getFullYear() - from.getFullYear()) * 12 +
    (now.getMonth() - from.getMonth());
  if (now.getDate() < from.getDate()) {
    months--;
  }
  return months;
};

// Products still in stock that were not purchased for the last N months
const AnalyseStock = ({ startLoading, stopLoading }) => {
  const [allWarehouses, setAllWarehouses] = useState([ALL_WAREHOUSES]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(ALL_WAREHOUSES);
  const [months, setMonths] = useState("6");
  const [minQuantity, setMinQuantity] = useState("0");
  const [rows, setRows] = useState(null);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [cutoffDate, setCutoffDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // parameters of the last "Analyser", reused when paging, sorting or searching
  const [analysis, setAnalysis] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(DEFAULT_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);
  const [sort, setSort] = useState({ sortBy: undefined, sortDirection: "ASC" });
  const searchTimeoutRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorAlertText, setErrorAlertText] = useState();

  useEffect(() => {
    WarehouseService.getAllRealWarehouse(getCurrentUserId(), 0)
      .then((response) => {
        if (response.data.success) {
          setAllWarehouses([
            ALL_WAREHOUSES,
            ...response.data.success.map((element) => ({
              value: element.id,
              label: element.name,
            })),
          ]);
        }
      })
      .catch((e) => {
        console.log("error in getAllRealWarehouse", e);
      });
  }, []);

  const showError = (text) => {
    setErrorAlertText(text);
    setOpenErrorAlert(true);
  };

  const handleErrorClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenErrorAlert(false);
  };

  const fetchPage = (params, page, size, q, sortParams) => {
    setIsLoading(true);
    startLoading();
    WarehouseService.analyseStock(getCurrentUserId(), {
      ...params,
      page: page - 1, // API uses 0-based page indexing
      size: size,
      q: q.trim() || undefined,
      sortBy: sortParams.sortBy,
      sortDirection: sortParams.sortDirection,
    })
      .then((response) => {
        if (response.data.products) {
          setRows(
            response.data.products.map((row) => ({
              ...row,
              rowKey: `${row.idWarehouse}-${row.productId}`,
              months: monthsSince(row.lastPurchaseDate),
            }))
          );
          setTotalItems(response.data.totalItems);
          setTotalQuantity(response.data.totalQuantity);
          setCutoffDate(response.data.cutoffDate);
        } else {
          setRows(null);
          showError("Erreur : " + (response.data.error || "analyse impossible"));
        }
      })
      .catch((e) => {
        setRows(null);
        showError("Erreur : " + (e.response?.data?.error || e));
      })
      .finally(() => {
        setIsLoading(false);
        stopLoading();
      });
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const onAnalyseHandler = () => {
    const monthsValue = parseInt(months, 10);
    const minQuantityValue = minQuantity === "" ? 0 : Number(minQuantity);
    if (!monthsValue || monthsValue < 1) {
      showError("Erreur : le nombre de mois doit être au moins 1");
      return;
    }
    if (isNaN(minQuantityValue) || minQuantityValue < 0) {
      showError("Erreur : la quantité minimale doit être positive");
      return;
    }

    const params = {
      months: monthsValue,
      minQuantity: minQuantityValue,
      idWarehouse: selectedWarehouse.value || undefined,
    };
    setAnalysis(params);
    setCurrentPage(1);
    fetchPage(params, 1, sizePerPage, searchQuery, sort);
  };

  const onSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (!analysis) {
      return;
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchPage(analysis, 1, sizePerPage, value, sort);
    }, 500);
  };

  const handleTableChange = (type, { page, sizePerPage: newSize, sortField, sortOrder }) => {
    if (!analysis) {
      return;
    }
    if (type === "sort") {
      const sortParams = {
        sortBy: sortField,
        sortDirection: sortOrder === "desc" ? "DESC" : "ASC",
      };
      setSort(sortParams);
      setCurrentPage(1);
      fetchPage(analysis, 1, sizePerPage, searchQuery, sortParams);
    } else if (type === "pagination") {
      // changing the page size goes back to the first page
      const nextPage = newSize !== sizePerPage ? 1 : page;
      setCurrentPage(nextPage);
      setSizePerPage(newSize);
      fetchPage(analysis, nextPage, newSize, searchQuery, sort);
    }
  };

  // the table holds one page: the export reads every page of the current
  // analysis, with the search and sort in use
  const onExportExcelHandler = async () => {
    if (!analysis) {
      return;
    }
    setExporting(true);
    try {
      const fetchExportPage = async (page) => {
        const response = await WarehouseService.analyseStock(getCurrentUserId(), {
          ...analysis,
          page: page,
          size: EXPORT_PAGE_SIZE,
          q: searchQuery.trim() || undefined,
          sortBy: sort.sortBy,
          sortDirection: sort.sortDirection,
        });
        if (!response.data.products) {
          throw new Error(response.data.error || "export impossible");
        }
        return response.data;
      };
      // first page gives the page count, the others are loaded 5 at a time
      const first = await fetchExportPage(0);
      const pages = [first.products];
      for (let start = 1; start < first.totalPages; start += 5) {
        const batch = [];
        for (let page = start; page < Math.min(start + 5, first.totalPages); page++) {
          batch.push(fetchExportPage(page));
        }
        (await Promise.all(batch)).forEach((data) => pages.push(data.products));
      }
      const all = pages.flat();
      const data = all.map((row) => ({
        "Code article": row.productCode ?? "",
        "Code à barre": row.barcode ?? "",
        "Libellé": row.name ?? "",
        "Famille & Marque": row.description ?? "",
        "Dépôt": row.warehouseName ?? "",
        "Quantité": Number(row.quantity),
        "Dernier achat": dateFormatter(row.lastPurchaseDate),
        "Mois sans achat": monthsSince(row.lastPurchaseDate),
      }));
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Analyse");
      XLSX.writeFile(workbook, `analyse_stock_${analysis.months}mois.xlsx`);
    } catch (e) {
      showError("Erreur : " + (e.response?.data?.error || e.message || e));
    } finally {
      setExporting(false);
    }
  };

  const dateFormatter = (cell) =>
    cell ? new Date(cell).toLocaleDateString("fr-FR") : "";

  const columns = [
    { dataField: "productCode", text: "Code article", sort: true },
    { dataField: "barcode", text: "Code à barre", sort: true },
    { dataField: "name", text: "Libellé", sort: true },
    { dataField: "description", text: "Famille & Marque" },
    { dataField: "warehouseName", text: "Dépôt", sort: true },
    {
      dataField: "quantity",
      text: "Quantité",
      sort: true,
      align: "center",
      headerAlign: "center",
      formatter: (cell) => Number(cell),
    },
    {
      dataField: "lastPurchaseDate",
      text: "Dernier achat",
      sort: true,
      align: "center",
      headerAlign: "center",
      formatter: dateFormatter,
    },
    {
      dataField: "months",
      text: "Mois sans achat",
      align: "center",
      headerAlign: "center",
    },
  ];

  const pageButtonRenderer = ({
    page,
    active,
    disable,
    title,
    onPageChange,
  }) => {
    const handleClick = (e) => {
      e.preventDefault();
      onPageChange(page);
    };
    if (page === "...") {
      return (
        <li className="page-item disabled">
          <span className="page-link">...</span>
        </li>
      );
    }
    const activeStyle = active
      ? { backgroundColor: "black", color: "white" }
      : { backgroundColor: "white", color: "black" };
    return (
      <li className="page-item">
        <button
          className="btn btn-dark"
          onClick={handleClick}
          style={activeStyle}
          disabled={disable}
          title={title}
        >
          {page}
        </button>
      </li>
    );
  };

  const options = {
    page: currentPage,
    sizePerPage: sizePerPage,
    totalSize: totalItems,
    pageButtonRenderer,
    showTotal: true,
    paginationSize: 5,
    withFirstAndLast: true,
    sizePerPageList: [
      { text: "20", value: 20 },
      { text: "50", value: 50 },
      { text: "100", value: 100 },
    ],
  };

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-6"></div>
            <div className="col-md-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/" style={{ color: "black" }}>
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/EtatStocks" style={{ color: "black" }}>
                    Etat des stocks
                  </Link>
                </li>
                <li
                  className="breadcrumb-item active"
                  style={{ color: "black" }}
                >
                  Analyser stock
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-dark">
            <div className="card-header">
              <h3 className="card-title">Analyser stock</h3>
            </div>
            <div className="card-body">
              <p>
                Produits encore en stock dont le dernier achat (tous dépôts
                confondus) date de plus que le nombre de mois choisi, avec une
                quantité supérieure à la quantité minimale dans le dépôt.
              </p>
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Nombre de mois</label>
                    <TextField
                      style={{ width: "100%" }}
                      variant="outlined"
                      size="small"
                      type="number"
                      inputProps={{ min: 1 }}
                      value={months}
                      onChange={(e) => setMonths(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Quantité minimale</label>
                    <TextField
                      style={{ width: "100%" }}
                      variant="outlined"
                      size="small"
                      type="number"
                      inputProps={{ min: 0 }}
                      value={minQuantity}
                      onChange={(e) => setMinQuantity(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Dépôt</label>
                    <Select
                      value={selectedWarehouse}
                      onChange={setSelectedWarehouse}
                      options={allWarehouses}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="card-footer">
              <div className="text-center">
                <button
                  className="btn btn-dark"
                  onClick={onAnalyseHandler}
                  disabled={isLoading}
                >
                  <i className="fas fa-search"></i>{" "}
                  {isLoading ? "Analyse en cours..." : "Analyser"}
                </button>
              </div>
            </div>
          </div>

          {rows && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  {totalItems} produit(s) — quantité totale{" "}
                  {Number(totalQuantity)}
                  {cutoffDate &&
                    ` — pas d'achat depuis le ${dateFormatter(cutoffDate)}`}
                </h3>
                <button
                  className="btn btn-dark float-right"
                  disabled={exporting || totalItems === 0}
                  onClick={onExportExcelHandler}
                >
                  <i className="fas fa-download"></i>{" "}
                  {exporting ? "Génération..." : "Télécharger Excel"}
                </button>
              </div>
              <div className="card-body">
                <div className="row ml-0 mb-3 align-items-center">
                  <div className="input-group" style={{ width: "250px" }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Chercher par nom, code, barre..."
                      value={searchQuery}
                      onChange={onSearchChange}
                      style={{ borderRight: "none" }}
                    />
                    <div className="input-group-append">
                      <span
                        className="input-group-text"
                        style={{ backgroundColor: "white", borderLeft: "none" }}
                      >
                        {isLoading ? (
                          <i className="fas fa-spinner fa-spin text-dark"></i>
                        ) : (
                          <i className="fas fa-search text-dark"></i>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
                <BootstrapTable
                  keyField="rowKey"
                  data={rows}
                  remote={{ pagination: true, sort: true, filter: false }}
                  onTableChange={handleTableChange}
                  columns={columns}
                  pagination={paginationFactory(options)}
                  striped
                  hover
                  condensed
                  noDataIndication={() => (
                    <div className="text-center py-4">
                      <i className="fas fa-box-open fa-2x text-muted mb-2"></i>
                      <p>Aucun produit trouvé</p>
                    </div>
                  )}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      <Snackbar
        open={openErrorAlert}
        autoHideDuration={4000}
        onClose={handleErrorClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert variant="filled" onClose={handleErrorClose} severity="error">
          {errorAlertText}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default withLoader(AnalyseStock);
